import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';
import { Category, EventEnrollment } from '../../types/content';
import Button from '../../components/UI/Button';
import ActionStatus from '../../components/UI/ActionStatus';
import Input from '../../components/UI/Input';
import Card from '../../components/UI/Card';
import { canApprove, canWriteEvents } from '../../utils/roles';
import { useAuth } from '../../context/AuthContext';
import { UnsavedLeaveDialog, useDirtyBaseline, useUnsavedChangesGuard } from '../../components/content/UnsavedLeaveDialog';
import { useAutoClear } from '../../hooks/useAutoClear';

const toLocal = (value?: string | null) => (value ? value.slice(0, 16) : '');

const EventEditorPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const eventId = id ? Number(id) : undefined;
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [summary, setSummary] = useState('');
  const [mode, setMode] = useState('Online');
  const [location, setLocation] = useState('');
  const [meetingLink, setMeetingLink] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [price, setPrice] = useState('0');
  const [currency, setCurrency] = useState('INR');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [speakerName, setSpeakerName] = useState('');
  const [speakerTitle, setSpeakerTitle] = useState('');
  const [speakerCompany, setSpeakerCompany] = useState('');
  const [coverImageId, setCoverImageId] = useState<number | undefined>();
  const [coverImageUrl, setCoverImageUrl] = useState<string | undefined>();
  const [categories, setCategories] = useState<Category[]>([]);
  const [enrollments, setEnrollments] = useState<EventEnrollment[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useAutoClear(message, setMessage, 5000);
  useAutoClear(error, setError, 10000);
  const [loading, setLoading] = useState(!!eventId);

  useEffect(() => {
    contentApi.listCategories('Event').then((r) => setCategories(r.data.categories || [])).catch(() => undefined);
  }, []);

  const loadEnrollments = (idValue: number) => {
    contentApi.eventEnrollments({ eventId: idValue }).then((r) => setEnrollments(r.data.enrollments || [])).catch(() => undefined);
  };

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    contentApi.getEvent(eventId).then((r) => {
      const item = r.data.event;
      setTitle(item.title);
      setSubtitle(item.subtitle || '');
      setSummary(item.summary || '');
      setMode(item.mode || 'Online');
      setLocation(item.location || '');
      setMeetingLink(item.meetingLink || '');
      setStartsAt(toLocal(item.startsAt));
      setEndsAt(toLocal(item.endsAt));
      setPrice(String(item.price ?? 0));
      setCurrency(item.currency || 'INR');
      setCategoryId(item.categoryId || '');
      setSpeakerName(item.speakerName || '');
      setSpeakerTitle(item.speakerTitle || '');
      setSpeakerCompany(item.speakerCompany || '');
      setCoverImageId(item.coverImageId || undefined);
      setCoverImageUrl(item.coverImageUrl || undefined);
    }).catch(() => setError('Unable to load this event.')).finally(() => setLoading(false));
    loadEnrollments(eventId);
  }, [eventId]);

  const formState = useMemo(() => ({
    title, subtitle, summary, mode, location, meetingLink, startsAt, endsAt, price, currency, categoryId,
    speakerName, speakerTitle, speakerCompany, coverImageId,
  }), [title, subtitle, summary, mode, location, meetingLink, startsAt, endsAt, price, currency, categoryId, speakerName, speakerTitle, speakerCompany, coverImageId]);
  const { isDirty, markClean } = useDirtyBaseline(formState, !loading);
  const { leaveOpen, stay, leaveWithoutSaving } = useUnsavedChangesGuard(isDirty);

  const payload = () => ({
    title,
    subtitle,
    summary,
    mode,
    location: mode === 'Offline' ? location : location || null,
    meetingLink: mode === 'Online' ? meetingLink : null,
    startsAt: startsAt ? new Date(startsAt).toISOString() : null,
    endsAt: endsAt ? new Date(endsAt).toISOString() : null,
    price: Number(price) || 0,
    currency,
    categoryId: categoryId === '' ? null : categoryId,
    speakerName: speakerName || null,
    speakerTitle: speakerTitle || null,
    speakerCompany: speakerCompany || null,
    coverImageId: coverImageId || null,
  });

  const save = async (publish = false) => {
    setSaving(true);
    setError(null);
    try {
      const response = await contentApi.saveEvent(payload(), eventId);
      const savedId = response.data.event.id;
      if (publish) {
        await contentApi.submitEvent(savedId);
        setMessage(canApprove(user) ? 'Event published.' : 'Submitted for review.');
      } else {
        setMessage('Event saved.');
      }
      markClean();
      if (!eventId) navigate(`/admin/events/${savedId}/edit`, { replace: true });
      else loadEnrollments(savedId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const decide = async (enrollmentId: number, decision: string) => {
    await contentApi.decideEventEnrollment(enrollmentId, decision);
    if (eventId) loadEnrollments(eventId);
  };

  if (!canWriteEvents(user)) {
    return <div className="max-w-4xl mx-auto px-4 py-10 text-gray-600">You do not have permission to create events.</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/admin/dashboard?tab=events" className="inline-flex items-center text-sm font-medium text-primary-700 hover:text-primary-800">← Back to events</Link>
          <h1 className="mt-2 text-3xl font-bold text-gray-900">{eventId ? 'Edit event' : 'New event'}</h1>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex gap-2">
          <Button type="button" variant="outline" isLoading={saving} onClick={() => save(false)}>Save</Button>
          <Button type="button" isLoading={saving} onClick={() => save(true)}>{canApprove(user) ? 'Save and publish' : 'Save and submit'}</Button>
          </div>
          <ActionStatus className="w-full max-w-md" message={message} error={error} />
        </div>
      </div>
      <p className="mt-2 text-gray-600">Online meeting links are shown only to approved enrolled users. You can add or change the link later.</p>
      <Card className="mt-6 p-6 space-y-4">
        <Input id="event-title" label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Input id="event-subtitle" label="Subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
        <label className="block text-sm font-medium text-gray-700">Summary
          <textarea className="mt-1 w-full rounded-md border-gray-300" rows={4} value={summary} onChange={(e) => setSummary(e.target.value)} />
        </label>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="block text-sm font-medium text-gray-700">Online or offline
            <select className="mt-1 w-full rounded-md border-gray-300" value={mode} onChange={(e) => setMode(e.target.value)}>
              <option>Online</option>
              <option>Offline</option>
            </select>
          </label>
          <label className="block text-sm font-medium text-gray-700">Category
            <select className="mt-1 w-full rounded-md border-gray-300" value={categoryId} onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">None</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        </div>
        {mode === 'Online' ? (
          <Input id="event-link" label="Meeting link (enrolled users only)" value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)} placeholder="https://" />
        ) : (
          <Input id="event-location" label="Physical location" value={location} onChange={(e) => setLocation(e.target.value)} />
        )}
        <div className="grid md:grid-cols-3 gap-4">
          <Input id="event-speaker" label="Speaker name" value={speakerName} onChange={(e) => setSpeakerName(e.target.value)} />
          <Input id="event-speaker-title" label="Professional role" value={speakerTitle} onChange={(e) => setSpeakerTitle(e.target.value)} placeholder="Principal Engineer" />
          <Input id="event-speaker-company" label="Company" value={speakerCompany} onChange={(e) => setSpeakerCompany(e.target.value)} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Input id="event-start" label="Starts" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
          <Input id="event-end" label="Ends" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Input id="event-price" type="number" label="Price (INR)" value={price} onChange={(e) => setPrice(e.target.value)} />
          <Input id="event-currency" label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)} />
        </div>
        <label className="block text-sm font-medium text-gray-700">Thumbnail
          <img
            src={coverImageUrl || '/images/highlights-team.png'}
            alt=""
            className="mt-2 h-32 w-full max-w-sm object-cover rounded-lg bg-gray-100"
          />
          <input
            type="file"
            accept="image/*"
            className="mt-1 block text-sm"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const response = await contentApi.uploadMedia(file, title || file.name);
                setCoverImageId(response.data.media.id);
                setCoverImageUrl(response.data.media.url);
              } catch {
                setError('Thumbnail upload failed.');
              }
            }}
          />
        </label>
      </Card>

      {eventId ? (
        <Card className="mt-8 p-6">
          <h2 className="text-xl font-semibold text-gray-900">Enrollment requests</h2>
          <p className="mt-1 text-sm text-gray-600">Approve, reject, or cancel registrations. Approved paid events wait for payment before the link is shown.</p>
          <div className="mt-4 space-y-3">
            {enrollments.length === 0 && <p className="text-gray-500">No enrollment requests yet.</p>}
            {enrollments.map((row) => (
              <div key={row.id} className="rounded-xl border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-900">{row.userEmail || row.userProfileId}</p>
                  <p className="text-sm text-gray-500">{row.status} · {row.currency} {row.payable}</p>
                </div>
                <div className="flex gap-2">
                  {row.status === 'Pending' && (
                    <>
                      <Button size="sm" onClick={() => decide(row.id, 'Approved')}>Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => decide(row.id, 'Rejected')}>Reject</Button>
                    </>
                  )}
                  {row.status !== 'Rejected' && row.status !== 'Cancelled' && (
                    <Button size="sm" variant="outline" onClick={() => decide(row.id, 'Rejected')}>Cancel registration</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : null}
      <UnsavedLeaveDialog open={leaveOpen} onStay={stay} onLeave={leaveWithoutSaving} />
    </div>
  );
};

export default EventEditorPage;
